import { useQueries } from '@tanstack/react-query';
import { useAuth } from './useAuth';

interface MasterDataCountsResult {
  counts: Record<string, number>;
  loading: Record<string, boolean>;
  errors: Record<string, boolean>;
}

export const useMasterDataCounts = (entities: string[]): MasterDataCountsResult => {
  const { axios } = useAuth();

  const queries = useQueries({
    queries: entities.map((entity) => ({
      queryKey: ['master-data-count', entity],
      queryFn: async () => {
        const response = await axios.get(`/api/master-data/${entity}`, {
          params: { limit: 1, page: 1 }, // Only get count, not full data
        });

        // Try to get count from pagination first, fallback to data length
        return (
          response.data.pagination?.total ??
          response.data.data?.data?.length ??
          response.data.data?.length ??
          0
        );
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
      refetchInterval: 2 * 60 * 1000, // Refresh every 2 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 2, // Retry failed requests twice
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    })),
  });

  // Aggregate results
  const counts: Record<string, number> = {};
  const loading: Record<string, boolean> = {};
  const errors: Record<string, boolean> = {};

  entities.forEach((entity, index) => {
    const query = queries[index];
    counts[entity] = (query.data as number | undefined) ?? 0;
    loading[entity] = query.isLoading;
    errors[entity] = query.isError;
  });

  return { counts, loading, errors };
};

export default useMasterDataCounts;
